import type { RouteLocationNormalized } from 'vue-router'
import { consola } from 'consola'

export function useImpersonate() {
  const impersonationCookie = useCookie('impersonation')
  const { refresh } = useAuth()
  const { setToken } = useAuthState()
  const localePath = useLocalePath()
  const { $i18n, $backend } = useNuxtApp()

  const config = useAppConfig()
  const { rawToken } = useAuthState()

  const impersonate = async (id: number) => {
    const { access_token: impersonatedAccessToken } = await $backend(
      '/api/auth/{user_id}/access_token',
      {
        method: 'POST',
        path: {
          user_id: id,
        },
      },
    )

    const redirectFn = config.auth.impersonateRedirectionUrl

    if (typeof redirectFn !== 'function') {
      consola.error(
        'In order to impersonate a user, you must define a users.impersonateRedirectionUrl(_id: number) function in the app.config.ts file.',
      )
      consola.error(
        'Since app.config.ts uses a function merger, so you must define a function that returns a function that returns a string: () => (_id: number) => string',
      )
      return
    }

    await navigateTo(
      {
        path: await redirectFn(id),
        query: {
          impersonatedAccessToken,
          impersonatedData: btoa(
            JSON.stringify({
              fromAccessToken: rawToken.value,
              fromLocale: $i18n.locale.value,
              fromPath: useRequestURL().href,
            }),
          ),
        },
      },
      { external: true },
    )
  }

  const initImpersonation = async (to: RouteLocationNormalized) => {
    const impersonatedAccessToken = to.query.impersonatedAccessToken as
      | string
      | undefined

    if (!impersonatedAccessToken) {
      return undefined
    }

    const redirectRoute = to.query.route as string | undefined

    // If the user was impersonated, this impersonation is actually him going back to his own account
    // So we don't need to store the impersonation data in the cookie
    if (!to.query.wasImpersonated) {
      impersonationCookie.value = to.query.impersonatedData as string
    }

    setToken(impersonatedAccessToken)
    await refresh()

    let path = to.path
    const locale = to.query.locale as any

    if (redirectRoute) {
      path = localePath({ name: redirectRoute ?? undefined }, locale)
    }

    if (locale) {
      $i18n.locale.value = locale
    }

    to.path = path

    delete to.query.impersonatedAccessToken
    delete to.query.impersonatedData
    delete to.query.wasImpersonated
    delete to.query.locale

    return to
  }

  const stopImpersonation = () => {
    const impersonatedData = impersonationCookie.value

    if (!impersonatedData) {
      return
    }

    impersonationCookie.value = undefined

    const impersonatedDataParsed = JSON.parse(atob(impersonatedData))

    const { fromAccessToken, fromLocale, fromPath } = impersonatedDataParsed

    return navigateTo(
      {
        path: fromPath,
        query: {
          impersonatedAccessToken: fromAccessToken,
          locale: fromLocale,
          wasImpersonated: 1,
        },
      },
      { external: true },
    )
  }

  const isImpersonating = computed(() => {
    return impersonationCookie.value !== undefined
  })

  return {
    impersonate,
    initImpersonation,
    isImpersonating,
    stopImpersonation,
  }
}
